/**
 * Accounts Zustand Store
 *
 * Manages all state for the Accounts & Users section including:
 * - Account list with filters and pagination
 * - Active account details
 * - Account CRUD operations
 * - Lifecycle actions (suspend, reactivate, cancel)
 * - Selection state for bulk actions
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  AccountListItem,
  AccountDetail,
  AccountListFilters,
  AccountSortField,
  PaginationMeta,
  TenantStatus,
  SuspendAccountDto,
  ReactivateAccountDto,
  CancelAccountDto,
  CreateAccountDto,
  UpdateAccountDto,
  AccountNote,
  CreateAccountNoteDto,
  AccountUsageStats,
  TenantDiscount,
  AssignDiscountDto,
} from '@respectly/shared-types';

/** Per-account user activity audit entry (from AuditLog table) */
export interface AccountAuditEntry {
  id: string;
  tenantId: string;
  userId: string | null;
  user: { id: string; firstName: string; lastName: string; email: string } | null;
  action: string;
  entityType: string;
  entityId: string;
  entityName: string | null;
  changes: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: Date;
}

// ============================================
// Types
// ============================================

interface AccountTrendDataPoint {
  date: string;
  total: number;
  active: number;
  trial: number;
}

/** Error state */
interface AccountsError {
  code: string;
  message: string;
}

/** Account user type */
interface AccountUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: Date | null;
  createdAt: Date;
}

// ============================================
// State Interface
// ============================================

export interface AccountsState {
  // Account List
  accounts: AccountListItem[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: AccountsError | null;

  // Filters & Sorting
  filters: AccountListFilters;
  sortBy: AccountSortField;
  sortOrder: 'asc' | 'desc';

  // Selection
  selectedIds: Set<string>;

  // Active Account
  activeAccountId: string | null;
  activeAccount: AccountDetail | null;
  isLoadingAccount: boolean;
  accountError: AccountsError | null;

  // Account Users
  accountUsers: AccountUser[];
  isLoadingUsers: boolean;

  // Account Usage
  accountUsage: AccountUsageStats | null;
  isLoadingUsage: boolean;

  // Account Audit Log
  accountAudit: AccountAuditEntry[];
  accountAuditPagination: { page: number; pageSize: number; total: number; totalPages: number } | null;
  accountAuditSearch: string;
  accountAuditEventType: string;
  isLoadingAudit: boolean;

  // Account Notes
  accountNotes: AccountNote[];
  isLoadingNotes: boolean;

  // Account Discounts
  accountDiscounts: TenantDiscount[];
  isLoadingDiscounts: boolean;

  // Account Growth Trend
  accountGrowthTrend: AccountTrendDataPoint[] | null;
  accountGrowthRate: number | null;
  isLoadingGrowthTrend: boolean;

  // Action States
  isPerformingAction: boolean;
  actionError: AccountsError | null;

  // Actions - List
  loadAccounts: (page?: number) => Promise<void>;
  refreshAccounts: () => Promise<void>;

  // Actions - Filters
  setFilter: <K extends keyof AccountListFilters>(key: K, value: AccountListFilters[K]) => void;
  clearFilters: () => void;
  setSorting: (field: AccountSortField, order: 'asc' | 'desc') => void;

  // Actions - Selection
  selectAccount: (id: string) => void;
  deselectAccount: (id: string) => void;
  toggleSelect: (id: string) => void;
  selectAll: () => void;
  clearSelection: () => void;

  // Actions - Active Account
  loadAccountDetail: (id: string) => Promise<void>;
  clearActiveAccount: () => void;

  // Actions - Account Users
  loadAccountUsers: (accountId: string) => Promise<void>;

  // Actions - Account Usage
  loadAccountUsage: (accountId: string) => Promise<void>;

  // Actions - Account Audit
  loadAccountAudit: (accountId: string, params?: { page?: number; search?: string; eventType?: string }) => Promise<void>;
  setAuditSearch: (accountId: string, search: string) => void;
  setAuditEventType: (accountId: string, eventType: string) => void;
  setAuditPage: (accountId: string, page: number) => void;

  // Actions - Account Notes
  loadAccountNotes: (accountId: string) => Promise<void>;
  addAccountNote: (accountId: string, dto: CreateAccountNoteDto) => Promise<boolean>;
  deleteAccountNote: (accountId: string, noteId: string) => Promise<boolean>;
  toggleNotePin: (accountId: string, noteId: string, isPinned: boolean) => Promise<boolean>;

  // Actions - Account Discounts
  loadAccountDiscounts: (accountId: string) => Promise<void>;
  assignDiscount: (accountId: string, dto: AssignDiscountDto) => Promise<boolean>;
  removeAccountDiscount: (accountId: string, assignmentId: string) => Promise<boolean>;

  // Actions - CRUD
  createAccount: (dto: CreateAccountDto) => Promise<string | null>;
  updateAccount: (id: string, dto: UpdateAccountDto) => Promise<boolean>;
  deleteAccount: (id: string) => Promise<boolean>;

  // Actions - Lifecycle
  suspendAccount: (id: string, dto: SuspendAccountDto) => Promise<boolean>;
  reactivateAccount: (id: string, dto?: ReactivateAccountDto) => Promise<boolean>;
  cancelAccount: (id: string, dto: CancelAccountDto) => Promise<boolean>;
  extendTrial: (id: string, days: number, reason?: string) => Promise<{ success: boolean; helcimSynced?: boolean; helcimSyncError?: string; newTrialEndsAt?: string }>;

  // Actions - Growth Trend
  loadAccountGrowthTrend: (months?: number) => Promise<void>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Account List
  accounts: [] as AccountListItem[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as AccountsError | null,

  // Filters & Sorting
  filters: {} as AccountListFilters,
  sortBy: 'createdAt' as AccountSortField,
  sortOrder: 'desc' as 'asc' | 'desc',

  // Selection
  selectedIds: new Set<string>(),

  // Active Account
  activeAccountId: null as string | null,
  activeAccount: null as AccountDetail | null,
  isLoadingAccount: false,
  accountError: null as AccountsError | null,

  // Account Users
  accountUsers: [] as AccountUser[],
  isLoadingUsers: false,

  // Account Usage
  accountUsage: null as AccountUsageStats | null,
  isLoadingUsage: false,

  // Account Audit Log
  accountAudit: [] as AccountAuditEntry[],
  accountAuditPagination: null as { page: number; pageSize: number; total: number; totalPages: number } | null,
  accountAuditSearch: '',
  accountAuditEventType: '',
  isLoadingAudit: false,

  // Account Notes
  accountNotes: [] as AccountNote[],
  isLoadingNotes: false,

  // Account Discounts
  accountDiscounts: [] as TenantDiscount[],
  isLoadingDiscounts: false,

  // Account Growth Trend
  accountGrowthTrend: null as AccountTrendDataPoint[] | null,
  accountGrowthRate: null as number | null,
  isLoadingGrowthTrend: false,

  // Action States
  isPerformingAction: false,
  actionError: null as AccountsError | null,
});

// ============================================
// Store
// ============================================

export const useAccountsStore = create<AccountsState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // Account List Actions
  // ============================================

  loadAccounts: async (page = 1) => {
    set({ isLoadingList: true, listError: null });

    try {
      const api = getApiClient();
      const { filters, sortBy, sortOrder } = get();

      // Build query params
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);

      // Apply filters
      if (filters.search) {
        params.set('search', filters.search);
      }
      if (filters.status) {
        params.set('status', filters.status);
      }
      if (filters.billingStatus) {
        params.set('billingStatus', filters.billingStatus);
      }
      if (filters.planId) {
        params.set('planId', filters.planId);
      }
      if (filters.assignedToId) {
        params.set('assignedToId', filters.assignedToId);
      }
      if (filters.source) {
        params.set('source', filters.source);
      }
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }

      const response = await api.get<{
        data: AccountListItem[];
        pagination: PaginationMeta;
      }>(`${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.LIST}?${params.toString()}`);

      set({
        accounts: response.data,
        pagination: response.pagination,
        isLoadingList: false,
      });
    } catch (error) {
      console.error('Error loading accounts:', error);
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_ACCOUNTS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load accounts',
        },
      });
    }
  },

  refreshAccounts: async () => {
    const { pagination } = get();
    await get().loadAccounts(pagination?.page || 1);
  },

  // ============================================
  // Filter Actions
  // ============================================

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadAccounts(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadAccounts(1);
  },

  setSorting: (field, order) => {
    set({ sortBy: field, sortOrder: order });
    get().loadAccounts(1);
  },

  // ============================================
  // Selection Actions
  // ============================================

  selectAccount: (id: string) => {
    set((state) => {
      const newSelection = new Set(state.selectedIds);
      newSelection.add(id);
      return { selectedIds: newSelection };
    });
  },

  deselectAccount: (id: string) => {
    set((state) => {
      const newSelection = new Set(state.selectedIds);
      newSelection.delete(id);
      return { selectedIds: newSelection };
    });
  },

  toggleSelect: (id: string) => {
    const { selectedIds } = get();
    if (selectedIds.has(id)) {
      get().deselectAccount(id);
    } else {
      get().selectAccount(id);
    }
  },

  selectAll: () => {
    const { accounts } = get();
    set({ selectedIds: new Set(accounts.map((a) => a.id)) });
  },

  clearSelection: () => {
    set({ selectedIds: new Set() });
  },

  // ============================================
  // Active Account Actions
  // ============================================

  loadAccountDetail: async (id: string) => {
    set({
      activeAccountId: id,
      isLoadingAccount: true,
      accountError: null,
    });

    try {
      const api = getApiClient();
      const response = await api.get<AccountDetail>(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.GET(id));

      set({
        activeAccount: response,
        isLoadingAccount: false,
      });
    } catch (error) {
      console.error('Error loading account detail:', error);
      set({
        isLoadingAccount: false,
        accountError: {
          code: 'LOAD_ACCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load account details',
        },
      });
    }
  },

  clearActiveAccount: () => {
    set({
      activeAccountId: null,
      activeAccount: null,
      accountUsers: [],
      accountUsage: null,
      accountAudit: [],
      accountAuditPagination: null,
      accountAuditSearch: '',
      accountAuditEventType: '',
      accountNotes: [],
    });
  },

  // ============================================
  // Account Users Actions
  // ============================================

  loadAccountUsers: async (accountId: string) => {
    set({ isLoadingUsers: true });

    try {
      const api = getApiClient();
      const response = await api.get<AccountUser[]>(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.USERS(accountId));

      set({
        accountUsers: response,
        isLoadingUsers: false,
      });
    } catch (error) {
      console.error('Error loading account users:', error);
      set({ isLoadingUsers: false });
    }
  },

  // ============================================
  // Account Usage Actions
  // ============================================

  loadAccountUsage: async (accountId: string) => {
    set({ isLoadingUsage: true });

    try {
      const api = getApiClient();
      const response = await api.get<AccountUsageStats>(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.USAGE(accountId));

      set({
        accountUsage: response,
        isLoadingUsage: false,
      });
    } catch (error) {
      console.error('Error loading account usage:', error);
      set({ isLoadingUsage: false });
    }
  },

  // ============================================
  // Account Audit Actions
  // ============================================

  loadAccountAudit: async (accountId: string, params?: { page?: number; search?: string; eventType?: string }) => {
    set({ isLoadingAudit: true });

    try {
      const api = getApiClient();
      const { accountAuditSearch, accountAuditEventType } = get();
      const page = params?.page ?? 1;
      const search = params?.search ?? accountAuditSearch;
      const eventType = params?.eventType ?? accountAuditEventType;

      const qs = new URLSearchParams();
      qs.set('page', page.toString());
      qs.set('pageSize', '20');
      if (search) qs.set('search', search);
      if (eventType) qs.set('eventType', eventType);

      const response = await api.get<{
        events: AccountAuditEntry[];
        pagination: { page: number; pageSize: number; total: number; totalPages: number };
      }>(`${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.AUDIT(accountId)}?${qs.toString()}`);

      set({
        accountAudit: response.events,
        accountAuditPagination: response.pagination,
        isLoadingAudit: false,
      });
    } catch (error) {
      console.error('Error loading account audit:', error);
      set({ isLoadingAudit: false });
    }
  },

  setAuditSearch: (accountId: string, search: string) => {
    set({ accountAuditSearch: search });
    get().loadAccountAudit(accountId, { page: 1, search });
  },

  setAuditEventType: (accountId: string, eventType: string) => {
    set({ accountAuditEventType: eventType });
    get().loadAccountAudit(accountId, { page: 1, eventType });
  },

  setAuditPage: (accountId: string, page: number) => {
    get().loadAccountAudit(accountId, { page });
  },

  // ============================================
  // Account Notes Actions
  // ============================================

  loadAccountNotes: async (accountId: string) => {
    set({ isLoadingNotes: true });

    try {
      const api = getApiClient();
      const response = await api.get<AccountNote[]>(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.NOTES(accountId));

      set({
        accountNotes: response,
        isLoadingNotes: false,
      });
    } catch (error) {
      console.error('Error loading account notes:', error);
      set({ isLoadingNotes: false });
    }
  },

  addAccountNote: async (accountId: string, dto: CreateAccountNoteDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      const response = await api.post<AccountNote>(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.NOTES(accountId), dto);

      // Add note to local state
      set((state) => ({
        accountNotes: [response, ...state.accountNotes],
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      console.error('Error adding account note:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'ADD_NOTE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to add note',
        },
      });
      return false;
    }
  },

  deleteAccountNote: async (accountId: string, noteId: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.NOTE(accountId, noteId));

      // Remove note from local state
      set((state) => ({
        accountNotes: state.accountNotes.filter((n) => n.id !== noteId),
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      console.error('Error deleting account note:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DELETE_NOTE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete note',
        },
      });
      return false;
    }
  },

  toggleNotePin: async (
    accountId: string,
    noteId: string,
    isPinned: boolean
  ): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      await api.patch(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.NOTE(accountId, noteId), { isPinned });

      // Update note in local state
      set((state) => ({
        accountNotes: state.accountNotes.map((n) =>
          n.id === noteId ? { ...n, isPinned } : n
        ),
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      console.error('Error toggling note pin:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'TOGGLE_PIN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to toggle pin',
        },
      });
      return false;
    }
  },

  // ============================================
  // Account Discounts Actions
  // ============================================

  loadAccountDiscounts: async (accountId: string) => {
    set({ isLoadingDiscounts: true });

    try {
      const api = getApiClient();
      const response = await api.get<TenantDiscount[]>(
        API_ENDPOINTS.SUPER_ADMIN.DISCOUNTS.TENANT_DISCOUNTS(accountId)
      );

      set({ accountDiscounts: response, isLoadingDiscounts: false });
    } catch (error) {
      console.error('Error loading account discounts:', error);
      set({ isLoadingDiscounts: false });
    }
  },

  assignDiscount: async (accountId: string, dto: AssignDiscountDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      const response = await api.post<TenantDiscount>(
        API_ENDPOINTS.SUPER_ADMIN.DISCOUNTS.ASSIGN_TO_TENANT(accountId),
        dto
      );

      // Re-assigning an existing (removed) discount returns the same assignment id,
      // so de-dupe before prepending.
      set((state) => ({
        accountDiscounts: [
          response,
          ...state.accountDiscounts.filter((d) => d.id !== response.id),
        ],
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      console.error('Error assigning discount:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'ASSIGN_DISCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to assign discount',
        },
      });
      return false;
    }
  },

  removeAccountDiscount: async (accountId: string, assignmentId: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      await api.delete(
        API_ENDPOINTS.SUPER_ADMIN.DISCOUNTS.REMOVE_FROM_TENANT(accountId, assignmentId)
      );

      set((state) => ({
        accountDiscounts: state.accountDiscounts.filter((d) => d.id !== assignmentId),
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      console.error('Error removing discount:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'REMOVE_DISCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to remove discount',
        },
      });
      return false;
    }
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createAccount: async (dto: CreateAccountDto): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      const response = await api.post<AccountDetail>(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.CREATE, dto);

      set({ isPerformingAction: false });

      // Refresh accounts list
      await get().loadAccounts(1);

      return response.id;
    } catch (error) {
      console.error('Error creating account:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CREATE_ACCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create account',
        },
      });
      return null;
    }
  },

  updateAccount: async (id: string, dto: UpdateAccountDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      await api.patch(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.UPDATE(id), dto);

      set({ isPerformingAction: false });

      // Refresh account detail if viewing
      if (get().activeAccountId === id) {
        await get().loadAccountDetail(id);
      }

      // Refresh accounts list
      await get().refreshAccounts();

      return true;
    } catch (error) {
      console.error('Error updating account:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_ACCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update account',
        },
      });
      return false;
    }
  },

  deleteAccount: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.DELETE(id));

      set({ isPerformingAction: false });

      // Clear active account if deleted
      if (get().activeAccountId === id) {
        get().clearActiveAccount();
      }

      // Remove from selection
      get().deselectAccount(id);

      // Refresh accounts list
      await get().refreshAccounts();

      return true;
    } catch (error) {
      console.error('Error deleting account:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DELETE_ACCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete account',
        },
      });
      return false;
    }
  },

  // ============================================
  // Lifecycle Actions
  // ============================================

  suspendAccount: async (id: string, dto: SuspendAccountDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.SUSPEND(id), dto);

      // Update local state optimistically
      set((state) => ({
        accounts: state.accounts.map((a) =>
          a.id === id ? { ...a, status: 'suspended' as TenantStatus } : a
        ),
        activeAccount:
          state.activeAccount?.id === id
            ? { ...state.activeAccount, status: 'suspended' as TenantStatus }
            : state.activeAccount,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      console.error('Error suspending account:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'SUSPEND_ACCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to suspend account',
        },
      });
      return false;
    }
  },

  extendTrial: async (id, days, reason) => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      const res = await api.post<{
        message: string;
        newTrialEndsAt: string;
        helcimSynced: boolean;
        helcimSyncError?: string;
      }>(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.EXTEND_TRIAL(id), { days, reason });

      const newTrialEndsAt = res.newTrialEndsAt ? new Date(res.newTrialEndsAt) : null;

      set((state) => ({
        accounts: state.accounts.map((a) =>
          a.id === id ? { ...a, trialEndsAt: newTrialEndsAt } : a,
        ),
        activeAccount:
          state.activeAccount?.id === id
            ? { ...state.activeAccount, trialEndsAt: newTrialEndsAt }
            : state.activeAccount,
        isPerformingAction: false,
      }));

      return {
        success: true,
        helcimSynced: res.helcimSynced,
        helcimSyncError: res.helcimSyncError,
        newTrialEndsAt: res.newTrialEndsAt,
      };
    } catch (error) {
      console.error('Error extending trial:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'EXTEND_TRIAL_FAILED',
          message: error instanceof Error ? error.message : 'Failed to extend trial',
        },
      });
      return { success: false };
    }
  },

  reactivateAccount: async (id: string, dto?: ReactivateAccountDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.REACTIVATE(id), dto || {});

      // Update local state optimistically
      set((state) => ({
        accounts: state.accounts.map((a) =>
          a.id === id ? { ...a, status: 'active' as TenantStatus } : a
        ),
        activeAccount:
          state.activeAccount?.id === id
            ? { ...state.activeAccount, status: 'active' as TenantStatus }
            : state.activeAccount,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      console.error('Error reactivating account:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'REACTIVATE_ACCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to reactivate account',
        },
      });
      return false;
    }
  },

  cancelAccount: async (id: string, dto: CancelAccountDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });

    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.CANCEL(id), dto);

      // Update local state optimistically
      set((state) => ({
        accounts: state.accounts.map((a) =>
          a.id === id ? { ...a, status: 'cancelled' as TenantStatus } : a
        ),
        activeAccount:
          state.activeAccount?.id === id
            ? { ...state.activeAccount, status: 'cancelled' as TenantStatus }
            : state.activeAccount,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      console.error('Error cancelling account:', error);
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CANCEL_ACCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to cancel account',
        },
      });
      return false;
    }
  },

  // ============================================
  // Growth Trend Action
  // ============================================

  loadAccountGrowthTrend: async (months = 12) => {
    set({ isLoadingGrowthTrend: true });
    try {
      const api = getApiClient();
      const response = await api.get<{
        points: AccountTrendDataPoint[];
        growthRate: number;
      }>(`${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.GROWTH_TREND}?months=${months}`);

      set({
        accountGrowthTrend: response.points,
        accountGrowthRate: response.growthRate,
        isLoadingGrowthTrend: false,
      });
    } catch (error) {
      console.error('Error loading account growth trend:', error);
      set({ isLoadingGrowthTrend: false });
    }
  },

  // ============================================
  // Utility
  // ============================================

  clearError: () => {
    set({ listError: null, accountError: null, actionError: null });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get account count by status */
export const useAccountCountByStatus = (status: TenantStatus) => {
  return useAccountsStore((state) =>
    state.accounts.filter((a) => a.status === status).length
  );
};

/** Get selected accounts */
export const useSelectedAccounts = () => {
  return useAccountsStore((state) =>
    state.accounts.filter((a) => state.selectedIds.has(a.id))
  );
};

/** Check if any loading state is active */
export const useIsAccountsLoading = () => {
  const isLoadingList = useAccountsStore((state) => state.isLoadingList);
  const isLoadingAccount = useAccountsStore((state) => state.isLoadingAccount);
  const isPerformingAction = useAccountsStore((state) => state.isPerformingAction);
  return isLoadingList || isLoadingAccount || isPerformingAction;
};

/** Get pinned notes */
export const usePinnedNotes = () => {
  return useAccountsStore((state) => state.accountNotes.filter((n) => n.isPinned));
};
